import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { MoreVertical, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import {
  createPortalApp,
  deletePortalApp,
  fetchPortalApps,
  type PortalAppFieldRecord,
  type PortalAppRecord,
  updatePortalApp,
} from '../../api/apps';
import { useAuth } from '../../context/AuthProvider';

type PortalAppField = {
  id: string;
  name: string;
  value: string;
};

type PortalApp = {
  id: string;
  name: string;
  url: string;
  mark: string;
  accent: string;
  iconSrc: string;
  fields: PortalAppField[];
};

type AppDraft = {
  name: string;
  url: string;
  iconSrc: string | null;
  fields: PortalAppField[];
};

const SKELETON_COUNT = 8;

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const Wrap = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Header = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.4)};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Eyebrow = styled.span`
  color: rgba(224, 161, 94, 0.92);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: #f5f7fb;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 760px;
  color: rgba(218, 222, 231, 0.8);
  font-size: 1rem;
  line-height: 1.6;
  font-weight: 700;
`;

const AddButton = styled.button`
  min-height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(224, 161, 94, 0.36);
  background: transparent;
  color: #f5f7fb;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  font-weight: 900;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.8);
    background: rgba(255, 255, 255, 0.03);
  }
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: start;
`;

const AppTile = styled.div<{ $clickable: boolean }>`
  position: relative;
  width: 8rem;
  aspect-ratio: 1 / 1;
  display: grid;
  justify-items: center;
  align-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(1)}`};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: transparent;
  min-height: 0;
  box-shadow: none;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable }) =>
    $clickable
      ? `
          &:hover {
            border-color: rgba(224, 161, 94, 0.28);
          }
        `
      : ''}
`;

const AppMenuAnchor = styled.div`
  position: absolute;
  right: 0px;
`;

const AppMenuButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  color: #f5f7fb;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const AppMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 132px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #15181d;
  display: grid;
  padding: 6px;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  z-index: 3;
`;

const AppMenuItem = styled.button<{ $danger?: boolean }>`
  min-height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? '#f6b1ab' : '#f5f7fb')};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? 'rgba(166, 76, 68, 0.18)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const LogoMark = styled.div<{ $accent: string }>`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: ${({ $accent }) => $accent};
  color: #ffffff;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const AppName = styled.span`
  color: #f5f7fb;
  font-size: 0.84rem;
  font-weight: 800;
  text-align: center;
  line-height: 1.35;
`;

const SkeletonTile = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: grid;
  justify-items: center;
  align-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(1)}`};
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  min-height: 0;
`;

const SkeletonBlock = styled.div<{ $width: string; $height: string; $radius: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: ${({ $radius }) => $radius};
  background:
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.05) 0%,
      rgba(255, 255, 255, 0.12) 50%,
      rgba(255, 255, 255, 0.05) 100%
    );
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s linear infinite;
`;

const EmptyState = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  color: rgba(218, 222, 231, 0.68);
  font-size: 0.95rem;
  font-weight: 700;
  text-align: center;
  padding: ${({ theme }) => theme.spacing(3)};
`;

const ErrorText = styled.p`
  margin: 0;
  color: #f6b1ab;
  font-size: 0.88rem;
  font-weight: 800;
  line-height: 1.5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(7, 10, 14, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalCard = styled.div`
  width: min(760px, 100%);
  height: min(80vh, 760px);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #111317;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalHeaderCopy = styled.div`
  display: grid;
  gap: 6px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #f5f7fb;
  font-size: 1.1rem;
  font-weight: 900;
`;

const ModalText = styled.p`
  margin: 0;
  color: rgba(218, 222, 231, 0.78);
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 700;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  color: #f5f7fb;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const ModalBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 20px 22px;
  display: grid;
  gap: 18px;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 18px;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const FieldLabel = styled.label`
  color: #f5f7fb;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const FieldInput = styled.input`
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #f5f7fb;
  padding: 0 12px;
  font-size: 0.92rem;
  font-weight: 700;
  outline: none;

  &::placeholder {
    color: rgba(218, 222, 231, 0.36);
  }

  &:focus {
    border-color: rgba(224, 161, 94, 0.64);
  }
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const UploadPreview = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  display: grid;
  place-items: center;
  overflow: hidden;
  color: rgba(245, 247, 251, 0.92);
  font-size: 0.9rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  min-height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: #f5f7fb;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
`;

const MutedText = styled.span`
  color: rgba(218, 222, 231, 0.58);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.45;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FieldsStack = styled.div`
  display: grid;
  gap: 12px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const RemoveFieldButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(166, 76, 68, 0.34);
  background: transparent;
  color: #f5f7fb;
  display: grid;
  place-items: center;
  cursor: pointer;

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 22px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const FooterNote = styled.p`
  margin: 0;
  color: rgba(218, 222, 231, 0.62);
  font-size: 0.8rem;
  font-weight: 700;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(224, 161, 94, 0.34);
  background: #e0a15e;
  color: #111317;
  font-size: 0.84rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

function createEmptyField(): PortalAppField {
  return {
    id: `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    value: '',
  };
}

function createEmptyDraft(): AppDraft {
  return {
    name: '',
    url: '',
    iconSrc: null,
    fields: [createEmptyField()],
  };
}

function createDraftFromApp(app: PortalApp): AppDraft {
  return {
    name: app.name,
    url: app.url,
    iconSrc: app.iconSrc,
    fields: app.fields.length
      ? app.fields.map((field) => ({ ...field }))
      : [createEmptyField()],
  };
}

function getInitials(value: string): string {
  const tokens = value
    .split(/[\s/_-]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (!tokens.length) return 'AP';
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0] ?? ''}${tokens[1][0] ?? ''}`.toUpperCase();
}

function mapPortalAppFields(fields: PortalAppFieldRecord[]): PortalAppField[] {
  return fields.length
    ? fields.map((field, index) => ({
        id: `field-${Date.now()}-${index + 1}-${Math.random().toString(36).slice(2, 8)}`,
        name: field.name,
        value: field.value,
      }))
    : [createEmptyField()];
}

function mapPortalAppRecord(record: PortalAppRecord): PortalApp {
  return {
    id: record.id,
    name: record.name,
    url: record.url,
    mark: getInitials(record.name),
    accent: '#1f2937',
    iconSrc: record.iconSrc,
    fields: mapPortalAppFields(record.fields),
  };
}

function normalizeExternalUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
}

export default function AppsPage() {
  const { role } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const appMenuRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState<PortalApp[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<AppDraft>(() => createEmptyDraft());
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [openMenuAppId, setOpenMenuAppId] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadApps = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await fetchPortalApps();
        if (cancelled) return;
        setApps(response.apps.map(mapPortalAppRecord));
      } catch (error) {
        if (cancelled) return;
        setApps([]);
        setLoadError(error instanceof Error ? error.message : 'Failed to load apps.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadApps();

    return () => {
      cancelled = true;
    };
  }, []);

  const resetDraft = () => {
    setDraft(createEmptyDraft());
    setModalError(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAppId(null);
    resetDraft();
  };

  const openModal = () => {
    setModalOpen(true);
    setEditingAppId(null);
    resetDraft();
  };

  useEffect(() => {
    if (!openMenuAppId) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!appMenuRef.current?.contains(event.target as Node)) {
        setOpenMenuAppId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openMenuAppId]);

  const handleIconSelection = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const iconSrc = await readFileAsDataUrl(file);
      setDraft((current) => ({ ...current, iconSrc }));
    } finally {
      event.target.value = '';
    }
  };

  const addField = () => {
    setDraft((current) => ({
      ...current,
      fields: [...current.fields, createEmptyField()],
    }));
  };

  const updateField = (fieldId: string, key: 'name' | 'value', value: string) => {
    setDraft((current) => ({
      ...current,
      fields: current.fields.map((field) =>
        field.id === fieldId ? { ...field, [key]: value } : field,
      ),
    }));
  };

  const removeField = (fieldId: string) => {
    setDraft((current) => {
      const nextFields = current.fields.filter((field) => field.id !== fieldId);
      return {
        ...current,
        fields: nextFields.length ? nextFields : [createEmptyField()],
      };
    });
  };

  const saveApp = async () => {
    const name = draft.name.trim();
    const url = normalizeExternalUrl(draft.url);
    const iconSrc = draft.iconSrc;
    if (!iconSrc || !name || !url) return;

    const normalizedFields = draft.fields
      .map((field) => ({
        ...field,
        name: field.name.trim(),
        value: field.value.trim(),
      }))
      .filter((field) => field.name.length > 0 || field.value.length > 0)
      .filter((field) => field.name.length > 0 && field.value.length > 0);

    setSaving(true);
    setModalError(null);
    try {
      const payload = {
        name,
        url,
        iconSrc,
        fields: normalizedFields.map((field) => ({
          name: field.name,
          value: field.value,
        })),
      };
      const response = editingAppId
        ? await updatePortalApp(editingAppId, payload)
        : await createPortalApp(payload);
      const nextApp = mapPortalAppRecord(response.app);
      setApps((current) =>
        editingAppId
          ? current.map((app) => (app.id === editingAppId ? nextApp : app))
          : [...current, nextApp],
      );
      closeModal();
    } catch (error) {
      setModalError(error instanceof Error ? error.message : 'Failed to save app.');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (app: PortalApp) => {
    setDraft(createDraftFromApp(app));
    setEditingAppId(app.id);
    setOpenMenuAppId(null);
    setModalError(null);
    setModalOpen(true);
  };

  const deleteApp = async (appId: string) => {
    setDeletingAppId(appId);
    setLoadError(null);
    try {
      await deletePortalApp(appId);
      setApps((current) => current.filter((app) => app.id !== appId));
      setOpenMenuAppId(null);
      if (editingAppId === appId) {
        closeModal();
      }
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to delete app.');
    } finally {
      setDeletingAppId(null);
    }
  };

  const modalTitle = editingAppId ? 'Edit App' : 'Add App';
  const saveLabel = editingAppId ? 'Save Changes' : 'Save App';
  const isAppFormValid = Boolean(draft.iconSrc && draft.name.trim() && draft.url.trim());

  return (
    <>
      <Wrap>
        <Header>
          <HeaderTop>
            <HeaderCopy>
              <Eyebrow>Operations</Eyebrow>
              <Title>Apps</Title>
              <Subtitle>
                Quick access to the operational tools the team uses every day. Apps added here are
                shared across the portal for admins and agents.
              </Subtitle>
            </HeaderCopy>

            {role === 'admin' ? (
              <AddButton type="button" onClick={openModal}>
                <Plus size={16} />
                Add App
              </AddButton>
            ) : null}
          </HeaderTop>
        </Header>

        {loadError && apps.length ? <ErrorText>{loadError}</ErrorText> : null}

        <AppsGrid>
          {loading
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <SkeletonTile key={`skeleton-${index + 1}`}>
                  <SkeletonBlock $width="64px" $height="64px" $radius="18px" />
                  <SkeletonBlock $width="72px" $height="12px" $radius="999px" />
                </SkeletonTile>
              ))
            : apps.map((app) => (
                <AppTile
                  key={app.id}
                  $clickable={Boolean(app.url)}
                  onClick={() => {
                    if (!app.url) return;
                    window.open(app.url, '_blank', 'noopener,noreferrer');
                  }}
                  onKeyDown={(event) => {
                    if (!app.url) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      window.open(app.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  role={app.url ? 'link' : undefined}
                  tabIndex={app.url ? 0 : undefined}
                >
                  {role === 'admin' ? (
                    <AppMenuAnchor
                      ref={(node) => {
                        if (openMenuAppId === app.id) {
                          appMenuRef.current = node;
                        }
                      }}
                    >
                      <AppMenuButton
                        type="button"
                        disabled={deletingAppId === app.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenMenuAppId((current) => (current === app.id ? null : app.id));
                        }}
                        aria-haspopup="menu"
                        aria-expanded={openMenuAppId === app.id}
                        aria-label={`${app.name} actions`}
                      >
                        <MoreVertical size={15} />
                      </AppMenuButton>
                      {openMenuAppId === app.id ? (
                        <AppMenu role="menu" aria-label={`${app.name} actions`}>
                          <AppMenuItem
                            type="button"
                            role="menuitem"
                            disabled={deletingAppId === app.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditModal(app);
                            }}
                          >
                            <Pencil size={14} />
                            Edit
                          </AppMenuItem>
                          <AppMenuItem
                            type="button"
                            role="menuitem"
                            $danger
                            disabled={deletingAppId === app.id}
                            onClick={async (event) => {
                              event.stopPropagation();
                              await deleteApp(app.id);
                            }}
                          >
                            <Trash2 size={14} />
                            Delete
                          </AppMenuItem>
                        </AppMenu>
                      ) : null}
                    </AppMenuAnchor>
                  ) : null}
                  <LogoMark $accent={app.accent} aria-hidden="true">
                    {app.iconSrc ? <img src={app.iconSrc} alt="" /> : app.mark}
                  </LogoMark>
                  <AppName>{app.name}</AppName>
                </AppTile>
              ))}
        </AppsGrid>
        {!loading && !apps.length ? (
          <EmptyState>
            {loadError
              ? loadError
              : role === 'admin'
                ? 'No apps added yet. Use Add App to create your first app.'
                : 'No apps have been added yet.'}
          </EmptyState>
        ) : null}
      </Wrap>

      {role === 'admin' && modalOpen ? (
        <ModalOverlay onMouseDown={closeModal}>
          <ModalCard onMouseDown={(event) => event.stopPropagation()}>
            <ModalHeader>
                <ModalHeaderCopy>
                  <ModalTitle>{modalTitle}</ModalTitle>
                  <ModalText>
                    Add a required app icon, app name, and app URL, plus any number of field
                    name/value pairs. The modal stays a fixed size and scrolls internally as more
                    fields are added.
                  </ModalText>
                </ModalHeaderCopy>
              <IconButton type="button" onClick={closeModal} aria-label="Close app modal">
                <X size={16} />
              </IconButton>
            </ModalHeader>

            <ModalBody>
              <FormGrid>
                <FieldGroup>
                  <FieldLabel>App Icon *</FieldLabel>
                  <UploadRow>
                    <UploadPreview>
                      {draft.iconSrc ? (
                        <img src={draft.iconSrc} alt="" />
                      ) : (
                        getInitials(draft.name.trim() || 'App')
                      )}
                    </UploadPreview>
                    <UploadActions>
                      <SecondaryButton
                        type="button"
                        onClick={() => {
                          fileInputRef.current?.click();
                        }}
                      >
                        <Upload size={14} />
                        Upload Icon
                      </SecondaryButton>
                      {draft.iconSrc ? (
                        <SecondaryButton
                          type="button"
                          onClick={() => {
                            setDraft((current) => ({ ...current, iconSrc: null }));
                          }}
                        >
                          Remove Icon
                        </SecondaryButton>
                      ) : null}
                      <MutedText>Required. PNG, JPG, or WebP.</MutedText>
                    </UploadActions>
                    <HiddenFileInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={(event) => {
                        void handleIconSelection(event);
                      }}
                    />
                  </UploadRow>
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="app-name">App Name *</FieldLabel>
                  <FieldInput
                    id="app-name"
                    required
                    value={draft.name}
                    onChange={(event) => {
                      const nextName = event.target.value;
                      setDraft((current) => ({ ...current, name: nextName }));
                    }}
                    placeholder="Insperity"
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="app-url">App URL *</FieldLabel>
                  <FieldInput
                    id="app-url"
                    required
                    value={draft.url}
                    onChange={(event) => {
                      const nextUrl = event.target.value;
                      setDraft((current) => ({ ...current, url: nextUrl }));
                    }}
                    placeholder="https://app.insperity.com"
                  />
                </FieldGroup>

                <FieldGroup>
                  <SectionHeader>
                    <FieldLabel as="span">Fields</FieldLabel>
                    <SecondaryButton type="button" onClick={addField}>
                      <Plus size={14} />
                      Add Field
                    </SecondaryButton>
                  </SectionHeader>

                  <FieldsStack>
                    {draft.fields.map((field) => (
                      <FieldRow key={field.id}>
                        <FieldInput
                          value={field.name}
                          onChange={(event) => updateField(field.id, 'name', event.target.value)}
                          placeholder="Field name"
                        />
                        <FieldInput
                          value={field.value}
                          onChange={(event) => updateField(field.id, 'value', event.target.value)}
                          placeholder="Field value"
                        />
                        <RemoveFieldButton
                          type="button"
                          onClick={() => removeField(field.id)}
                          aria-label="Remove field"
                          title="Remove field"
                        >
                          <Trash2 size={15} />
                        </RemoveFieldButton>
                      </FieldRow>
                    ))}
                  </FieldsStack>
                </FieldGroup>
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <div>
                <FooterNote>Apps now save to the shared portal and load across environments.</FooterNote>
                {modalError ? <ErrorText>{modalError}</ErrorText> : null}
              </div>
              <FooterActions>
                <SecondaryButton type="button" onClick={closeModal} disabled={saving}>
                  Cancel
                </SecondaryButton>
                <SaveButton
                  type="button"
                  onClick={() => {
                    void saveApp();
                  }}
                  disabled={!isAppFormValid || saving}
                >
                  {saving ? 'Saving...' : saveLabel}
                </SaveButton>
              </FooterActions>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </>
  );
}
